import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingAccordionGroup } from "./OnboardingAccordionGroup";
import { OnboardingItemKey } from "../../common/enums";
import styles from "./styles/onboarding.module.scss";

/** Routes where the setup guide pill/card should be completely hidden. */
const HIDDEN_ROUTES = ["/cashdesk"];

const ease = [0.32, 0.72, 0, 1];

export const OnboardingSetupGuide = () => {
  const onboarding = useOnboarding();
  const wasMinimizedRef = useRef(false);
  const hadActiveStepRef = useRef(false);

  const activeStepId = onboarding?.activeStepId ?? null;
  const setMinimized = onboarding?.setMinimized;
  const isMinimized = onboarding?.isMinimized ?? true;
  const hideForSession = onboarding?.hideForSession;

  const visibleGroups = onboarding?.visibleGroups ?? [];
  const visibleSteps = onboarding?.visibleSteps ?? [];
  const pendingItemKeys =
    onboarding?.pendingItemKeys ?? new Set<OnboardingItemKey>();
  const totalSteps = onboarding?.totalSteps ?? 0;
  const completedCount = onboarding?.completedCount ?? 0;
  const activateStep = onboarding?.activateStep;

  // Auto-minimize when a step overlay is active, restore when it ends
  useEffect(() => {
    if (!setMinimized) return;

    if (activeStepId) {
      hadActiveStepRef.current = true;
      wasMinimizedRef.current = isMinimized;
      if (!isMinimized) setMinimized(true);
    } else if (hadActiveStepRef.current) {
      // Only restore when transitioning away from an active step, not on mount
      setMinimized(wasMinimizedRef.current);
    }
    // Only react to activeStepId changes, not isMinimized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepId, setMinimized]);

  // First incomplete group should be expanded by default
  const firstIncompleteGroupId = useMemo(() => {
    for (const group of visibleGroups) {
      const groupSteps = visibleSteps.filter((s) => s.groupId === group.id);
      const allCompleted = groupSteps.every((s) => !pendingItemKeys.has(s.id));
      if (!allCompleted) return group.id;
    }
    return visibleGroups[0]?.id;
  }, [visibleGroups, visibleSteps, pendingItemKeys]);

  const location = useLocation();
  const navigate = useNavigate();
  const isHiddenRoute = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));

  const handleStepClick = useCallback(
    (stepId: OnboardingItemKey) => {
      if (stepId === OnboardingItemKey.AddBillingData) {
        navigate("/my-profile?tab=business");
      }
      // The cashdesk page decides which guide to show (the generic empty-state
      // message vs the real tour) based on whether products exist, so just
      // navigate there and let the page drive it — don't activate the tour here.
      if (stepId === OnboardingItemKey.CashdeskGuide) {
        navigate("/cashdesk");
        return;
      }
      activateStep?.(stepId);
    },
    [activateStep, navigate]
  );

  if (!onboarding || !onboarding.isActive || isHiddenRoute) return null;

  // All visible steps done — only hidden page guides remain.
  // Don't show the setup guide pill/card (page guides activate on their own).
  if (completedCount >= totalSteps) return null;

  const progressPercent =
    totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  return (
    <>
      {/* Pill — always in DOM, crossfades with card */}
      <AnimatePresence>
        {isMinimized && (
          <motion.div
            key="pill"
            className={styles.minimizedPillContainer}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease }}
          >
            <button
              className={styles.minimizedPill}
              onClick={() => setMinimized(false)}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M9 1V17M1 9H17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Ръководство
              <span className={styles.minimizedPillProgress}>
                {completedCount}/{totalSteps}
              </span>
            </button>
            <button
              className={styles.minimizedPillClose}
              onClick={() => hideForSession?.()}
              title="Скрий до следващата сесия"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card — always in DOM, crossfades with pill */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            key="card"
            className={styles.guideCard}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease }}
          >
            <div
              className={styles.guideCardHeader}
              onClick={() => setMinimized(true)}
            >
              <h3 className={styles.guideCardTitle}>
                Първи стъпки
              </h3>
              <div className={styles.guideCardActions}>
                <button
                  className={styles.guideCardActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMinimized(true);
                  }}
                  title="Минимизирай"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 8H12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <button
                  className={styles.guideCardActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    hideForSession?.();
                  }}
                  title="Скрий до следващата сесия"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M1 1L13 13M13 1L1 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className={styles.progressText}>
                {completedCount} от {totalSteps} стъпки
              </div>
            </div>

            <div className={styles.guideCardBody}>
              {visibleGroups.map((group) => {
                const groupSteps = visibleSteps.filter(
                  (s) => s.groupId === group.id
                );
                return (
                  <OnboardingAccordionGroup
                    key={group.id}
                    group={group}
                    steps={groupSteps}
                    pendingItemKeys={pendingItemKeys}
                    activeStepId={activeStepId}
                    defaultExpanded={group.id === firstIncompleteGroupId}
                    onStepClick={handleStepClick}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
